<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  broadcast: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'activate', 'pause']);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const STATUS_CLASSES = {
  draft: 'bg-n-slate-3 text-n-slate-11',
  active: 'bg-n-teal-3 text-n-teal-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-slate-3 text-n-slate-12',
};

const statusLabel = computed(() =>
  t(
    `CAMPAIGN.WHATSAPP_LITE.CARD.STATUS.${props.broadcast.status.toUpperCase()}`
  )
);

const metrics = computed(() => props.broadcast.metrics || {});

const audienceLabel = computed(() => {
  const filters = props.broadcast.audience_filters || {};
  if (filters.all_contacts)
    return t('CAMPAIGN.WHATSAPP_LITE.CARD.AUDIENCE.ALL_CONTACTS');
  const parts = [];
  if (filters.label_ids?.length) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_LITE.CARD.AUDIENCE.LABELS_COUNT', {
        count: filters.label_ids.length,
      })
    );
  }
  if (filters.kanban_stage_ids?.length) {
    parts.push(
      t('CAMPAIGN.WHATSAPP_LITE.CARD.AUDIENCE.KANBAN_COUNT', {
        count: filters.kanban_stage_ids.length,
      })
    );
  }
  return parts.join(' + ');
});

const canEdit = computed(() =>
  ['draft', 'paused'].includes(props.broadcast.status)
);
const canActivate = computed(() =>
  ['draft', 'paused'].includes(props.broadcast.status)
);
const canPause = computed(() => props.broadcast.status === 'active');
</script>

<template>
  <CardLayout>
    <div class="flex items-start justify-between w-full gap-3">
      <div class="flex flex-col min-w-0 gap-1">
        <div class="flex items-center gap-2">
          <span class="text-base font-medium text-n-slate-12 line-clamp-1">
            {{ broadcast.title }}
          </span>
          <span
            class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
            :class="STATUS_CLASSES[broadcast.status]"
          >
            {{ statusLabel }}
          </span>
        </div>
        <div
          v-dompurify-html="
            formatMessage(broadcast.message, false, false, false)
          "
          class="text-sm text-n-slate-11 line-clamp-2 [&>p]:mb-0"
        />
        <div
          class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-n-slate-11"
        >
          <span>{{ broadcast.inbox?.name }}</span>
          <span>{{ audienceLabel }}</span>
          <span>{{
            t('CAMPAIGN.WHATSAPP_LITE.CARD.DAILY_LIMIT', {
              count: broadcast.daily_limit,
            })
          }}</span>
        </div>
      </div>
      <div class="flex items-center flex-shrink-0 gap-2">
        <Button
          v-if="canActivate"
          variant="faded"
          color="teal"
          size="sm"
          :label="t('CAMPAIGN.WHATSAPP_LITE.CARD.ACTIONS.ACTIVATE')"
          @click="emit('activate')"
        />
        <Button
          v-if="canPause"
          variant="faded"
          color="amber"
          size="sm"
          :label="t('CAMPAIGN.WHATSAPP_LITE.CARD.ACTIONS.PAUSE')"
          @click="emit('pause')"
        />
        <Button
          v-if="canEdit"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-pencil"
          @click="emit('edit')"
        />
        <Button
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click="emit('delete')"
        />
      </div>
    </div>

    <div class="grid w-full grid-cols-5 gap-2 pt-2 mt-1 border-t border-n-weak">
      <div class="flex flex-col items-center">
        <span class="text-sm font-medium text-n-slate-12">{{
          metrics.total || 0
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          t('CAMPAIGN.WHATSAPP_LITE.CARD.METRICS.TOTAL')
        }}</span>
      </div>
      <div class="flex flex-col items-center">
        <span class="text-sm font-medium text-n-teal-11">{{
          metrics.sent || 0
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          t('CAMPAIGN.WHATSAPP_LITE.CARD.METRICS.SENT')
        }}</span>
      </div>
      <div class="flex flex-col items-center">
        <span class="text-sm font-medium text-n-ruby-11">{{
          metrics.failed || 0
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          t('CAMPAIGN.WHATSAPP_LITE.CARD.METRICS.FAILED')
        }}</span>
      </div>
      <div class="flex flex-col items-center">
        <span class="text-sm font-medium text-n-iris-11">{{
          metrics.replied || 0
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          t('CAMPAIGN.WHATSAPP_LITE.CARD.METRICS.REPLIED')
        }}</span>
      </div>
      <div class="flex flex-col items-center">
        <span class="text-sm font-medium text-n-slate-12">{{
          (metrics.pending || 0) + (metrics.scheduled || 0)
        }}</span>
        <span class="text-xs text-n-slate-11">{{
          t('CAMPAIGN.WHATSAPP_LITE.CARD.METRICS.PENDING')
        }}</span>
      </div>
    </div>
  </CardLayout>
</template>
